import React, { useCallback, useEffect, useMemo, useState } from "react";
import api from "../../api/client";
import Header from "../../components/layout/Header";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Badge } from "../../components/ui/badge";
import EmptyState from "../../components/common/EmptyState";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from "../../components/ui/dialog";
import { useAuth } from "../../context/AuthContext";
import { toast } from "sonner";
import {
  Award, Ban, CalendarDays, Copy, Eye, History,
  RefreshCw, Search, ShieldCheck, Users, XCircle,
} from "lucide-react";

const dateBR = (value) => {
  if (!value) return "—";
  try {
    const raw = String(value);
    const d = raw.length <= 10 ? new Date(`${raw.slice(0, 10)}T12:00:00`) : new Date(raw);
    return d.toLocaleDateString("pt-BR");
  } catch { return "—"; }
};

const dateTimeBR = (value) => {
  if (!value) return "—";
  try { return new Date(value).toLocaleString("pt-BR"); } catch { return "—"; }
};

const statusStyle = {
  "válido": "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/20",
  "vencido": "bg-amber-500/10 text-amber-600 dark:text-amber-400 border-amber-500/20",
  "revogado": "bg-destructive/10 text-destructive border-destructive/20",
  "validade não informada": "bg-muted text-muted-foreground border-border",
};

export default function EmissionsHistory() {
  const { isAdmin } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.get("/certificates/emissions?limit=300");
      setItems(response.data || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Falha ao carregar certificados emitidos");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (!term) return items;
    return items.filter((item) => {
      const participants = (item.participantes || []).join(" ");
      return `${item.lote_codigo || ""} ${item.cliente_nome || ""} ${item.treinamento_nome || ""} ${item.sigla || ""} ${participants}`
        .toLowerCase().includes(term);
    });
  }, [items, q]);

  const copyVerification = async (code) => {
    if (!code) return;
    const url = `${window.location.origin}/certificado/${encodeURIComponent(code)}`;
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Link público de validação copiado");
    } catch {
      window.prompt("Copie o link de validação:", url);
    }
  };

  const revoke = async (emission, certificateCode = null) => {
    if (!isAdmin) return;
    const label = certificateCode ? `certificado ${certificateCode}` : `lote ${emission.lote_codigo}`;
    const reason = window.prompt(`Motivo da revogação do ${label}:`, "") ?? "";
    if (!reason.trim()) return;
    if (reason.trim().length < 5) return toast.error("Informe um motivo com pelo menos 5 caracteres");
    if (!window.confirm(`Confirmar revogação do ${label}? O histórico será preservado e esta ação ficará registrada.`)) return;

    try {
      await api.post(`/certificates/emissions/${emission.id}/revoke`, {
        reason: reason.trim(),
        certificate_code: certificateCode || null,
      });
      toast.success(certificateCode ? "Certificado revogado" : "Lote revogado");
      await load();
      if (selected?.id === emission.id) {
        const fresh = await api.get(`/certificates/emissions/${emission.id}`);
        setSelected(fresh.data);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Falha ao revogar certificado");
    }
  };

  const totals = useMemo(() => ({
    lots: items.length,
    certificates: items.reduce((acc, item) => acc + Number(item.participantes_count || item.participantes?.length || 0), 0),
    revoked: items.filter((item) => item.status === "revogado").length,
  }), [items]);

  return (
    <>
      <Header
        title="Certificados emitidos"
        subtitle="Consulte certificados por cliente, treinamento, participante ou validade."
        actions={
          <Button variant="outline" onClick={load} className="px-3 sm:px-4">
            <RefreshCw className={`size-4 ${loading ? "animate-spin" : ""}`} /> <span className="hidden sm:inline">Atualizar</span>
          </Button>
        }
      />

      <div className="page-container space-y-5 sm:space-y-6" data-testid="certificate-emissions-page">
        <div className={`grid gap-3 ${totals.revoked > 0 ? "sm:grid-cols-3" : "sm:grid-cols-2"}`}>
          <Metric icon={Award} label="Certificados" value={totals.certificates} />
          <Metric icon={History} label="Lotes de treinamento" value={totals.lots} />
          {totals.revoked > 0 && <Metric icon={Ban} label="Lotes revogados" value={totals.revoked} alert />}
        </div>

        <div className="surface-panel flex flex-col gap-3 p-4 sm:flex-row sm:items-center sm:justify-between sm:p-5">
          <div className="relative w-full flex-1 sm:max-w-xl">
            <Search className="absolute left-3.5 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Buscar cliente, treinamento, participante ou lote…"
              className="pl-10"
            />
          </div>
          <div className="shrink-0 text-xs text-muted-foreground">{filtered.length} de {items.length} lote(s)</div>
        </div>

        {loading ? (
          <div className="space-y-3">
            {[0, 1, 2, 3].map((i) => <div key={i} className="h-20 animate-pulse rounded-2xl border border-border/70 bg-card/50" />)}
          </div>
        ) : filtered.length === 0 ? (
          <EmptyState icon={Award} title="Nenhum certificado encontrado" description={q ? "Tente outro termo de busca ou limpe a busca atual." : "Os certificados emitidos aparecerão aqui após a geração dos treinamentos."} />
        ) : (
          <>
            <div className="hidden overflow-x-auto rounded-2xl border border-border/80 bg-card/90 shadow-[0_1px_2px_hsl(var(--foreground)/0.03),0_18px_45px_hsl(var(--foreground)/0.035)] lg:block">
              <table className="w-full min-w-[980px] text-sm" data-ui-table>
                <thead>
                  <tr>
                    <th className="px-5 py-3.5 text-left">Lote</th>
                    <th className="px-5 py-3.5 text-left">Cliente</th>
                    <th className="px-5 py-3.5 text-left">Treinamento</th>
                    <th className="px-5 py-3.5 text-center">Certificados</th>
                    <th className="px-5 py-3.5 text-left">Conclusão / validade</th>
                    <th className="px-5 py-3.5 text-center">Status</th>
                    <th className="px-5 py-3.5 text-right">Ações</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border/70">
                  {filtered.map((item) => (
                    <tr key={item.id}>
                      <td className="px-5 py-4">
                        <div className="font-mono text-xs font-semibold text-primary">{item.lote_codigo || "Legado"}</div>
                        <div className="mt-1 text-[11px] text-muted-foreground">{dateTimeBR(item.emitted_at)}</div>
                      </td>
                      <td className="px-5 py-4 font-medium">{item.cliente_nome || "—"}</td>
                      <td className="px-5 py-4">
                        <div>{item.treinamento_nome || "—"}</div>
                        {item.sigla && <div className="mt-0.5 text-xs text-muted-foreground">{item.sigla}</div>}
                      </td>
                      <td className="px-5 py-4 text-center font-mono">{item.participantes_count || item.participantes?.length || 0}</td>
                      <td className="px-5 py-4 text-xs">
                        <div className="flex items-center gap-1.5"><CalendarDays className="size-3.5 text-primary" /> {dateBR(item.data_base)}</div>
                        <div className="mt-1 text-muted-foreground">até {dateBR(item.data_vencimento)}</div>
                      </td>
                      <td className="px-5 py-4 text-center">
                        <Badge variant="outline" className={statusStyle[item.status] || statusStyle["validade não informada"]}>{item.status}</Badge>
                      </td>
                      <td className="whitespace-nowrap px-5 py-4 text-right">
                        <Button variant="ghost" size="sm" onClick={() => setSelected(item)}><Eye className="size-4" /> Detalhes</Button>
                        {isAdmin && item.lote_codigo && item.status !== "revogado" && (
                          <Button variant="ghost" size="sm" className="text-destructive hover:text-destructive" onClick={() => revoke(item)}><Ban className="size-4" /> Revogar</Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="grid gap-3 lg:hidden">
              {filtered.map((item) => (
                <div key={item.id} className="surface-panel p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="font-mono text-xs font-semibold text-primary">{item.lote_codigo || "Emissão legada"}</div>
                      <div className="mt-1 truncate text-sm font-semibold">{item.cliente_nome || "—"}</div>
                      <div className="mt-0.5 truncate text-xs text-muted-foreground">{item.treinamento_nome || "—"}</div>
                    </div>
                    <Badge variant="outline" className={`${statusStyle[item.status] || statusStyle["validade não informada"]} shrink-0`}>{item.status}</Badge>
                  </div>

                  <div className="mt-4 grid grid-cols-3 gap-2 text-xs">
                    <Mini label="Certificados" value={item.participantes_count || item.participantes?.length || 0} />
                    <Mini label="Conclusão" value={dateBR(item.data_base)} />
                    <Mini label="Validade" value={dateBR(item.data_vencimento)} />
                  </div>

                  <div className="mt-4 flex items-center justify-between gap-2 border-t border-border/60 pt-3">
                    <div className="text-[10px] text-muted-foreground">{dateTimeBR(item.emitted_at)}</div>
                    <div className="flex items-center gap-1">
                      <Button variant="ghost" size="sm" onClick={() => setSelected(item)}><Eye className="size-4" /> Detalhes</Button>
                      {isAdmin && item.lote_codigo && item.status !== "revogado" && (
                        <Button variant="ghost" size="sm" className="text-destructive hover:text-destructive" onClick={() => revoke(item)}><Ban className="size-4" /></Button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>

      <Dialog open={!!selected} onOpenChange={(open) => !open && setSelected(null)}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>Certificados do lote {selected?.lote_codigo || "legado"}</DialogTitle>
            <DialogDescription>{selected?.cliente_nome} · {selected?.treinamento_nome}</DialogDescription>
          </DialogHeader>

          {selected && (
            <div className="space-y-5">
              <div className="grid gap-3 sm:grid-cols-3">
                <Mini label="Conclusão" value={dateBR(selected.data_base)} />
                <Mini label="Validade" value={dateBR(selected.data_vencimento)} />
                <Mini label="Participantes" value={selected.participantes_count || selected.participantes?.length || 0} />
              </div>

              {selected.revoked_at && (
                <div className="rounded-xl border border-destructive/25 bg-destructive/5 p-4">
                  <div className="flex items-center gap-2 font-medium text-destructive"><XCircle className="size-4" /> Lote revogado em {dateTimeBR(selected.revoked_at)}</div>
                  {selected.revocation_reason && <div className="mt-2 text-sm text-muted-foreground">Motivo: {selected.revocation_reason}</div>}
                  {selected.revoked_by && <div className="mt-1 text-xs text-muted-foreground">Responsável: {selected.revoked_by}</div>}
                </div>
              )}

              <div>
                <div className="mb-2 flex items-center gap-2 text-sm font-semibold"><Users className="size-4 text-primary" /> Participantes e certificados</div>
                <div className="overflow-hidden rounded-xl border border-border/80">
                  <div className="divide-y divide-border/70">
                    {(selected.certificates || []).map((cert) => (
                      <div key={cert.certificate_code || cert.participant} className="flex flex-col gap-3 p-3.5 sm:flex-row sm:items-center sm:justify-between">
                        <div className="min-w-0">
                          <div className="truncate text-sm font-medium">{cert.participant}</div>
                          <div className="mt-1 break-all font-mono text-[11px] text-muted-foreground">{cert.certificate_code || "Código indisponível em emissão legada"}</div>
                        </div>
                        <div className="flex shrink-0 items-center gap-1.5">
                          {cert.revoked ? (
                            <Badge variant="outline" className={statusStyle.revogado}><Ban className="mr-1 size-3" /> Revogado</Badge>
                          ) : cert.certificate_code ? (
                            <>
                              <Button variant="outline" size="sm" onClick={() => copyVerification(cert.certificate_code)}><Copy className="size-3.5" /> Copiar validação</Button>
                              {isAdmin && !selected.revoked_at && <Button variant="ghost" size="sm" className="text-destructive hover:text-destructive" onClick={() => revoke(selected, cert.certificate_code)}><Ban className="size-3.5" /> Revogar</Button>}
                            </>
                          ) : null}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {!!selected.revocation_history?.length && (
                <div>
                  <div className="mb-2 flex items-center gap-2 text-sm font-semibold"><ShieldCheck className="size-4 text-primary" /> Histórico de revogação</div>
                  <div className="space-y-2">
                    {selected.revocation_history.slice().reverse().map((entry, index) => (
                      <div key={`${entry.at}-${index}`} className="rounded-xl border border-border/70 bg-muted/20 p-3 text-xs">
                        <div className="font-medium">{entry.scope === "lot" ? "Lote revogado" : `Certificado ${entry.certificate_code} revogado`}</div>
                        <div className="mt-1 text-muted-foreground">{dateTimeBR(entry.at)} · {entry.actor || "Administrador"}</div>
                        {entry.reason && <div className="mt-1 text-muted-foreground">{entry.reason}</div>}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}

function Metric({ icon: Icon, label, value, alert = false }) {
  return (
    <div className={`relative min-w-0 overflow-hidden rounded-2xl border bg-card/90 p-4 shadow-[0_1px_2px_hsl(var(--foreground)/0.03)] ${alert ? "border-destructive/30" : "border-border/75"}`}>
      <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-primary/60 via-primary/15 to-transparent" />
      <div className="flex items-center justify-between gap-3">
        <span className="text-[10px] uppercase tracking-[0.12em] text-muted-foreground">{label}</span>
        <div className={`grid size-8 place-items-center rounded-lg ${alert ? "bg-destructive/10 text-destructive" : "bg-primary/10 text-primary"}`}><Icon className="size-4" /></div>
      </div>
      <div className="mt-3 break-words font-mono text-2xl font-semibold tracking-[-0.04em] tabular-nums">{value}</div>
    </div>
  );
}

function Mini({ label, value }) {
  return (
    <div className="min-w-0 rounded-xl border border-border/70 bg-muted/20 p-3">
      <div className="text-[9px] uppercase tracking-[0.12em] text-muted-foreground">{label}</div>
      <div className="mt-1 break-words font-medium">{value}</div>
    </div>
  );
}
